import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "./utils/supabaseClient";
import { getTenantBranding } from "./utils/tenantBranding";
import { isSupersededInvoice } from "./utils/formatHelpers";
import { getTakingCourseInSingapore, shouldApplyGst } from "./utils/locationBasedGst";
import {
  buildPortalDocumentsList,
  resolveProgramForRegistration,
  countCompletedPortalDocs,
  DEFAULT_LEGACY_SECTIONS,
} from "./utils/programDocuments";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Spinner,
  Text,
  useToast,
  VStack,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Container,
  Center,
  Heading,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Icon,
  Badge,
} from "@chakra-ui/react";
import { CheckCircleIcon, ViewIcon, DownloadIcon, ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { motion } from "framer-motion";
import moment from "moment";
import { FaFileAlt, FaFilePdf, FaCalendarAlt, FaClock, FaMapMarkerAlt, FaUser, FaGraduationCap, FaBook, FaInfoCircle, FaChalkboardTeacher, FaCheckCircle, FaHourglassHalf, FaCalendarPlus } from "react-icons/fa";

export const StudentPackage = () => {
  const { registrationID } = useParams();
  const location = useLocation();
  const [registrationData, setRegistrationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [activeDocument, setActiveDocument] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const [downloadedDocs, setDownloadedDocs] = useState(new Set()); // Track which documents have been downloaded
  const [isPolling, setIsPolling] = useState(false);
  const [currentDocCount, setCurrentDocCount] = useState(0);
  const [expandedModules, setExpandedModules] = useState(new Set()); // Track which modules are expanded
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false); // Show payment success alert
  const [orgGstSettings, setOrgGstSettings] = useState({ gst_enabled: false, gst_rate: 0 }); // Organization GST settings
  const pollIntervalRef = useRef(null);
  const hasTriggeredGenerationRef = useRef(false);
  
  // Check for payment success URL parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('payment') === 'success') {
      setShowPaymentSuccess(true);
      // Remove the query params from URL without refreshing
      window.history.replaceState({}, '', `/student-package/${registrationID}`);
    }
  }, [location.search, registrationID]);

  // Fetch organization GST settings
  useEffect(() => {
    const fetchOrganizationGstSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('organization')
          .select('gst_enabled, gst_rate')
          .eq('status', 'active')
          .single();
        
        if (error) {
          console.error('Error fetching organization GST settings:', error);
          return;
        }
        
        if (data) {
          setOrgGstSettings({
            gst_enabled: data.gst_enabled || false,
            gst_rate: parseFloat(data.gst_rate) || 0
          });
        }
      } catch (err) {
        console.error('Error fetching organization GST settings:', err);
      }
    };
    
    fetchOrganizationGstSettings();
  }, []);
  
  // Toggle module expansion
  const toggleModuleExpansion = (moduleId) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId);
      } else {
        newSet.add(moduleId);
      }
      return newSet;
    });
  };
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen: isDocOpen, onOpen: onDocOpen, onClose: onDocClose } = useDisclosure();

  console.log("Registration ID from URL:", registrationID);

  // --- Document Template Helper Functions ---
  const formatCurrency = (amount) => {
    const safe = Number.isFinite(Number(amount)) ? Number(amount) : 0;
    return new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD', minimumFractionDigits: 2 }).format(safe);
  };

  const formatInvoiceDate = (date) => {
    if (date == null || date === "") return "-";
    const m = moment(date);
    return m.isValid() ? m.format("DD MMM YYYY") : "-";
  };

  const getDueInBadge = (invoice) => {
    const isPaid = invoice.status === "paid" || invoice.status === "Paid";
    if (isPaid) return null;
    const due = moment(invoice.due_date).startOf("day");
    if (!invoice.due_date || !due.isValid()) return null;
    const today = moment().startOf("day");
    const days = due.diff(today, "days");
    if (days > 1) return { label: `In ${days} days`, bg: "blue.500", color: "white" };
    if (days === 1) return { label: "In 1 day", bg: "blue.500", color: "white" };
    if (days === 0) return { label: "Today", bg: "orange.500", color: "white" };
    if (days === -1) return { label: "1 day overdue", bg: "red.500", color: "white" };
    return { label: `${Math.abs(days)} days overdue`, bg: "red.500", color: "white" };
  };

  const generateInvoiceDueDatesICS = (invoices, certName) => {
    const now = moment().format("YYYYMMDDTHHmmss");
    const courseLabel = certName ? certName.trim() : "Course";
    const events = invoices.map((invoice, index) => {
      const due = moment(invoice.due_date).startOf("day");
      const dateStr = due.format("YYYY-MM-DD");
      const dueDateFormatted = due.format("DD MMM YYYY");
      const subtotal = invoice.invoice_line_items?.length > 0
        ? invoice.invoice_line_items.reduce((sum, item) => sum + (item.unit_price || 0) * (item.quantity || 0), 0) : 0;
      const gst = invoice.gst_amount || 0;
      const total = subtotal + gst;
      const amountStr = formatCurrency(total);
      const lineDesc = invoice.invoice_line_items?.length > 0
        ? invoice.invoice_line_items.map((item) => item.description).filter(Boolean).join(", ")
        : "";
      const descForSummary = lineDesc || "Invoice";
      const uid = `invoice-due-${invoice.id}-${index}-${Date.now()}@sozcode.com`;
      const summary = `${courseLabel} - Invoice due: ${descForSummary} (${amountStr})`;
      const descriptionParts = [
        `Course/Program: ${courseLabel}`,
        `Amount due: ${amountStr}`,
        `Due date: ${dueDateFormatted}`,
        lineDesc ? `Description: ${lineDesc}` : null,
        "Payment due by the date above.",
      ].filter(Boolean);
      const description = descriptionParts.join("\\n");
      const dtStart = moment(dateStr).format("YYYYMMDD");
      const dtEnd = moment(dateStr).add(1, "day").format("YYYYMMDD");
      const alarmDesc = `${descForSummary} (${amountStr}) for ${courseLabel} due tomorrow`;
      return ["BEGIN:VEVENT", `UID:${uid}`, `DTSTAMP:${now}`, `DTSTART;VALUE=DATE:${dtStart}`, `DTEND;VALUE=DATE:${dtEnd}`, `SUMMARY:${summary}`, `DESCRIPTION:${description}`, "STATUS:CONFIRMED", "BEGIN:VALARM", "TRIGGER:-P1D", "ACTION:DISPLAY", `DESCRIPTION:${alarmDesc}`, "END:VALARM", "END:VEVENT"].join("\r\n");
    });
    const calName = certName ? `Invoice due dates - ${certName}` : "Invoice due dates";
    return ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Sozcode LMS//Invoice Due Dates//EN", "CALSCALE:GREGORIAN", "METHOD:PUBLISH", `X-WR-CALNAME:${calName}`, ...events, "END:VCALENDAR"].join("\r\n");
  };

  const handleDownloadInvoiceDueDatesCalendar = (invoices, certName) => {
    const unpaid = invoices.filter((inv) => {
      if (isSupersededInvoice(inv)) return false;
      const paid = inv.status === "paid" || inv.status === "Paid";
      if (paid) return false;
      const due = moment(inv.due_date).startOf("day");
      return inv.due_date && due.isValid();
    });
    if (unpaid.length === 0) {
      toast({ title: "No due dates to add", description: "No unpaid invoices with a due date.", status: "info", duration: 3000, isClosable: true, position: "top" });
      return;
    }
    const name = (certName || "Invoices").replace(/\s+/g, "_");
    downloadICS(generateInvoiceDueDatesICS(unpaid, certName), `Invoice_Due_Dates_${name}.ics`);
    toast({ title: "Calendar file downloaded", description: `${unpaid.length} invoice due date(s) added to the file.`, status: "success", duration: 3000, isClosable: true, position: "top" });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB');
  };

  // Remove participant name suffix from document title for display only
  const getDisplayDocumentName = (title) => {
    if (!title) return '';
    // Remove patterns like "- John Doe", "-{{participant_name}}", "-{participant_name}", etc.
    // This removes anything after a dash followed by optional spaces
    return title.replace(/\s*-\s*.*$/, '').trim();
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return '';
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';
    const diffTime = Math.abs(end - start);
    const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30));
    return diffMonths.toString();
  };

  const getEffectiveGstEnabledForReg = (reg) =>
    shouldApplyGst({
      orgGstEnabled: !!orgGstSettings.gst_enabled,
      locationBasedGst: reg?.full_certification_runs?.full_certification?.location_based_gst === true,
      nationality: reg?.nationality,
      takingCourseInSingapore: getTakingCourseInSingapore(reg?.additional_data),
    });

  const calculateInstallmentSchedule = (reg) => {
    const subtotal = (reg?.full_certification_runs?.full_certification?.cost || 0) - (reg?.full_certification_runs?.full_certification?.discount || 0);
    
    // Apply GST if enabled for this student (location-based exemption aware)
    const applyGst = getEffectiveGstEnabledForReg(reg);
    const gstRate = applyGst ? (orgGstSettings.gst_rate / 100) : 0;
    const gstAmount = subtotal * gstRate;
    const totalFees = subtotal + gstAmount;
    
    const numInstallments = parseInt(reg?.full_certification_runs?.full_certification?.installments) || 1;
    const startDate = new Date(reg?.full_certification_runs?.start_date);
    const schedule = [];
    
    // Determine header text based on GST status
    const gstHeaderText = applyGst 
      ? `Amount (incl. ${orgGstSettings.gst_rate}% GST)<br/>(S$)` 
      : 'Amount<br/>(S$)';
    
    if (!Number.isFinite(totalFees) || Number.isNaN(startDate.getTime()) || numInstallments <= 0) {
      return `<table>
        <tr>
          <th>Instalment Schedule</th>
          <th>${gstHeaderText}</th>
          <th>Date Due</th>
        </tr>
      </table>`;
    }

    const installmentAmount = totalFees / numInstallments;
    let tableRows = '';
    for (let i = 0; i < numInstallments; i++) {
      const installmentDate = new Date(startDate);
      installmentDate.setMonth(installmentDate.getMonth() + i);
      tableRows += `
        <tr>
          <td>${i + 1}</td>
          <td>${formatCurrency(installmentAmount)}</td>
          <td>${formatDate(installmentDate)}</td>
        </tr>`;
    }

    return `<table>
      <tr>
        <th>Instalment Schedule</th>
        <th>${gstHeaderText}</th>
        <th>Date Due</th>
      </tr>
      ${tableRows}
    </table>`;
  };

  // Build HTML by replacing variables in template string
  const buildDocumentHtml = (templateString, variables) => {
    if (!templateString) return '';
    let html = templateString;
    Object.entries(variables).forEach(([key, val]) => {
      const safeVal = val == null ? '' : String(val);
      html = html.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), safeVal);
    });
    return html;
  };

  // Helper to format date for ICS (YYYYMMDD format)
  const formatICSDate = (dateStr) => {
    return moment(dateStr).format("YYYYMMDD");
  };

  // Helper to format datetime for ICS (YYYYMMDDTHHMMSS format)
  const formatICSDateTime = (dateStr, timeStr) => {
    if (!timeStr) return formatICSDate(dateStr);
    const datetime = moment(`${dateStr} ${timeStr}`, "YYYY-MM-DD HH:mm:ss");
    return datetime.format("YYYYMMDDTHHmmss");
  };

  // Generate ICS content for a single session
  const generateICSContent = (session, moduleName, courseTitle) => {
    const uid = `${session.session_id || session.id}-${Date.now()}@sozcode.com`;
    const now = moment().format("YYYYMMDDTHHmmss");
    
    const summary = `${moduleName} - ${courseTitle || "Training Session"}`;
    const location = [session.venue, session.room].filter(Boolean).join(", ") || "TBC";
    const description = [
      `Module: ${moduleName}`,
      session.trainer_name ? `Trainer: ${session.trainer_name}` : null,
      session.venue ? `Venue: ${session.venue}` : null,
      session.room ? `Room: ${session.room}` : null,
      session.venue_details?.address ? `Address: ${session.venue_details.address}` : null,
    ].filter(Boolean).join("\\n");

    let dtStart, dtEnd;
    if (session.start_time && session.end_time) {
      dtStart = formatICSDateTime(session.date, session.start_time);
      dtEnd = formatICSDateTime(session.date, session.end_time);
    } else {
      // All-day event
      dtStart = formatICSDate(session.date);
      dtEnd = formatICSDate(moment(session.date).add(1, 'day').format("YYYY-MM-DD"));
    }

    const icsContent = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Sozcode LMS//Course Session//EN",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "BEGIN:VEVENT",
      `UID:${uid}`,
      `DTSTAMP:${now}`,
      session.start_time ? `DTSTART:${dtStart}` : `DTSTART;VALUE=DATE:${dtStart}`,
      session.start_time ? `DTEND:${dtEnd}` : `DTEND;VALUE=DATE:${dtEnd}`,
      `SUMMARY:${summary}`,
      `DESCRIPTION:${description}`,
      `LOCATION:${location}`,
      "STATUS:CONFIRMED",
      "BEGIN:VALARM",
      "TRIGGER:-P1D",
      "ACTION:DISPLAY",
      "DESCRIPTION:Reminder: Training session tomorrow",
      "END:VALARM",
      "END:VEVENT",
      "END:VCALENDAR"
    ].join("\r\n");

    return icsContent;
  };

  // Generate ICS for multiple sessions (all sessions in a module)
  const generateMultipleICSContent = (sessions, moduleName, courseTitle) => {
    const now = moment().format("YYYYMMDDTHHmmss");
    
    const events = sessions.map((session, index) => {
      const uid = `${session.session_id || session.id}-${index}-${Date.now()}@sozcode.com`;
      const summary = `${moduleName} - Session ${index + 1}`;
      const location = [session.venue, session.room].filter(Boolean).join(", ") || "TBC";
      const description = [
        `Course: ${courseTitle || "Training"}`,
        `Module: ${moduleName}`,
        `Session: ${index + 1} of ${sessions.length}`,
        session.trainer_name ? `Trainer: ${session.trainer_name}` : null,
        session.venue ? `Venue: ${session.venue}` : null,
        session.room ? `Room: ${session.room}` : null,
        session.venue_details?.address ? `Address: ${session.venue_details.address}` : null,
      ].filter(Boolean).join("\\n");

      let dtStart, dtEnd;
      if (session.start_time && session.end_time) {
        dtStart = formatICSDateTime(session.date, session.start_time);
        dtEnd = formatICSDateTime(session.date, session.end_time);
      } else {
        dtStart = formatICSDate(session.date);
        dtEnd = formatICSDate(moment(session.date).add(1, 'day').format("YYYY-MM-DD"));
      }

      return [
        "BEGIN:VEVENT",
        `UID:${uid}`,
        `DTSTAMP:${now}`,
        session.start_time ? `DTSTART:${dtStart}` : `DTSTART;VALUE=DATE:${dtStart}`,
        session.start_time ? `DTEND:${dtEnd}` : `DTEND;VALUE=DATE:${dtEnd}`,
        `SUMMARY:${summary}`,
        `DESCRIPTION:${description}`,
        `LOCATION:${location}`,
        "STATUS:CONFIRMED",
        "BEGIN:VALARM",
        "TRIGGER:-P1D",
        "ACTION:DISPLAY",
        "DESCRIPTION:Reminder: Training session tomorrow",
        "END:VALARM",
        "END:VEVENT"
      ].join("\r\n");
    });

    const icsContent = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Sozcode LMS//Course Sessions//EN",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      `X-WR-CALNAME:${moduleName} - Training Sessions`,
      ...events,
      "END:VCALENDAR"
    ].join("\r\n");

    return icsContent;
  };

  // Download ICS file
  const downloadICS = (icsContent, filename) => {
    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handle single session calendar download
  const handleDownloadSessionCalendar = (session, moduleName, courseTitle) => {
    const icsContent = generateICSContent(session, moduleName, courseTitle);
    const dateStr = moment(session.date).format("YYYYMMDD");
    const filename = `${moduleName.replace(/\s+/g, "_")}_${dateStr}.ics`;
    downloadICS(icsContent, filename);
    toast({
      title: "Calendar Event Downloaded",
      description: "The session has been downloaded as an ICS file. Open it to add to your calendar.",
      status: "success",
      duration: 3000,
    });
  };

  // Handle all sessions calendar download for a module
  const handleDownloadModuleCalendar = (module, courseTitle) => {
    if (!module.sessions || module.sessions.length === 0) {
      toast({
        title: "No Sessions",
        description: "This module has no scheduled sessions yet.",
        status: "warning",
        duration: 3000,
      });
      return;
    }
    const moduleName = module.course_type?.name || module.course_type_code || "Module";
    const icsContent = generateMultipleICSContent(module.sessions, moduleName, courseTitle);
    const filename = `${moduleName.replace(/\s+/g, "_")}_All_Sessions.ics`;
    downloadICS(icsContent, filename);
    toast({
      title: "Calendar Events Downloaded",
      description: `${module.sessions.length} sessions have been downloaded. Open the file to add all to your calendar.`,
      status: "success",
      duration: 4000,
    });
  };

  // Handle download ALL sessions from ALL modules combined
  const handleDownloadAllModulesCalendar = (modules, courseTitle) => {
    // Collect all sessions from all modules
    const allSessions = [];
    modules.forEach(module => {
      if (module.sessions && module.sessions.length > 0) {
        const moduleName = module.course_type?.name || module.course_type_code || "Module";
        module.sessions.forEach((session, idx) => {
          allSessions.push({
            ...session,
            moduleName: moduleName,
            moduleIndex: modules.indexOf(module) + 1
          });
        });
      }
    });

    if (allSessions.length === 0) {
      toast({
        title: "No Sessions",
        description: "No scheduled sessions found across all modules.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    const now = moment().format("YYYYMMDDTHHmmss");
    
    const events = allSessions.map((session, index) => {
      const uid = `${session.session_id || session.id}-all-${index}-${Date.now()}@sozcode.com`;
      const summary = `${session.moduleName} - ${courseTitle || "Training"}`;
      const location = [session.venue, session.room].filter(Boolean).join(", ") || "TBC";
      const description = [
        `Course: ${courseTitle || "Training Program"}`,
        `Module: ${session.moduleName}`,
        session.trainer_name ? `Trainer: ${session.trainer_name}` : null,
        session.venue ? `Venue: ${session.venue}` : null,
        session.room ? `Room: ${session.room}` : null,
        session.venue_details?.address ? `Address: ${session.venue_details.address}` : null,
      ].filter(Boolean).join("\\n");

      let dtStart, dtEnd;
      if (session.start_time && session.end_time) {
        dtStart = formatICSDateTime(session.date, session.start_time);
        dtEnd = formatICSDateTime(session.date, session.end_time);
      } else {
        dtStart = formatICSDate(session.date);
        dtEnd = formatICSDate(moment(session.date).add(1, 'day').format("YYYY-MM-DD"));
      }

      return [
        "BEGIN:VEVENT",
        `UID:${uid}`,
        `DTSTAMP:${now}`,
        session.start_time ? `DTSTART:${dtStart}` : `DTSTART;VALUE=DATE:${dtStart}`,
        session.start_time ? `DTEND:${dtEnd}` : `DTEND;VALUE=DATE:${dtEnd}`,
        `SUMMARY:${summary}`,
        `DESCRIPTION:${description}`,
        `LOCATION:${location}`,
        "STATUS:CONFIRMED",
        "BEGIN:VALARM",
        "TRIGGER:-P1D",
        "ACTION:DISPLAY",
        "DESCRIPTION:Reminder: Training session tomorrow",
        "END:VALARM",
        "END:VEVENT"
      ].join("\r\n");
    });

    const icsContent = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Sozcode LMS//All Course Sessions//EN",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      `X-WR-CALNAME:${courseTitle || "Training Program"} - All Sessions`,
      ...events,
      "END:VCALENDAR"
    ].join("\r\n");

    const filename = `${(courseTitle || "Training").replace(/\s+/g, "_")}_All_Sessions.ics`;
    downloadICS(icsContent, filename);
    
    const moduleCount = modules.filter(m => m.sessions?.length > 0).length;
    toast({
      title: "All Calendar Events Downloaded",
      description: `${allSessions.length} sessions from ${moduleCount} modules have been downloaded. Open the file to add all to your calendar.`,
      status: "success",
      duration: 5000,
    });
  };

  // Prepare variables object from registration data
  const prepareDocumentVariables = (raw) => {
    if (!raw) return {};
    
    const courseFees = (raw.full_certification_runs?.full_certification?.cost) || 0;
    const discount = raw.full_certification_runs?.full_certification?.discount || 0;
    const subtotal = courseFees - discount;
    
    // Apply GST if enabled for this student (location-based exemption aware)
    const applyGst = getEffectiveGstEnabledForReg(raw);
    const gstRate = applyGst ? (orgGstSettings.gst_rate / 100) : 0;
    const gstAmount = subtotal * gstRate;
    const totalPayable = subtotal + gstAmount;
    
    const numInst = (raw.full_certification_runs?.full_certification?.installment_scheme) ? parseInt(String(raw.full_certification_runs?.full_certification?.installment_scheme).split(' ')[0], 10) : 1;
    const installmentschedule = calculateInstallmentSchedule(raw);

    const calculateFeeBreakdown = (raw) => {
      const courseFees = (raw?.full_certification_runs?.full_certification?.cost) || 0;
      const discount = raw?.full_certification_runs?.full_certification?.discount || 0;
      const subtotal = courseFees - discount;
      
      const applyGstInner = getEffectiveGstEnabledForReg(raw);
      const gstRateInner = applyGstInner ? (orgGstSettings.gst_rate / 100) : 0;
      const gstAmountInner = subtotal * gstRateInner;
      const totalPayableInner = subtotal + gstAmountInner;
      
      const numInst = parseInt(raw?.full_certification_runs?.full_certification?.installments) || 1;
      
      // Build GST row if enabled for this student
      const gstRow = applyGstInner ? `
        <tr>
          <td>GST (${orgGstSettings.gst_rate}%)</td>
          <td>${formatCurrency(gstAmountInner)}</td>
        </tr>` : '';
      
      return `<table>
        <tr>
          <td>Total Course Fees</td>
          <td>${formatCurrency(courseFees)}</td>
        </tr>
        <tr>
          <td>Medical Insurance</td>
          <td>$0.00</td>
        </tr>
        <tr>
          <td>Fee Protection Scheme Fee</td>
          <td>$0.00</td>
        </tr>
        <tr>
          <td>Discount</td>
          <td>${formatCurrency(discount)}</td>
        </tr>
        <tr>
          <td>Subtotal</td>
          <td>${formatCurrency(subtotal)}</td>
        </tr>${gstRow}
        <tr>
          <td>No of Instalments:</td>
          <td>${numInst}</td>
        </tr>
        <tr>
          <td><strong>Total Course Fees Payable${applyGstInner ? ' (incl. GST)' : ''}</strong></td>
          <td><strong>${formatCurrency(totalPayableInner)}</strong></td>
        </tr>
      </table>`;
    };
    
    const feeBreakdown = calculateFeeBreakdown(raw);

    // Add signature image if available
    const signatureHtml = raw.signature 
      ? `<img src="${raw.signature}" alt="Signature" style="max-width: 200px; height: auto;" />`
      : '';

    return {
      // PEI info (hardcoded)
      date_signed: formatDate(raw.date_signed),
      hr_line: '<hr style="border: 1px solid #000; margin: 20px 0; break-before: page;"/>',
      pei_name: getTenantBranding().name,
      pei_registration_number: getTenantBranding().uen,
      pei_address: getTenantBranding().address,
      pei_signatory_name: getTenantBranding().signatoryName,
      pei_signatory_designation: getTenantBranding().signatoryDesignation,
      nric: raw.nric || '',
      duration: raw.full_certification_runs?.full_certification?.duration + ' months' || '8 months',
      full_certification_name: raw.name || raw.full_certification_runs?.full_certification?.name || raw.certification_name || 'Course',
      full_certification_title: raw.name || raw.full_certification_runs?.full_certification?.name || raw.certification_name || 'Course',
      course_title: raw.name || raw.full_certification_runs?.full_certification?.name || raw.certification_name || 'Course',
      course_duration_months: calculateDuration(raw.start_date, raw.end_date) || '1',
      course_type: raw.registration_type || '',
      course_commencement_date: formatDate(raw.start_date),
      course_completion_date: formatDate(raw.end_date),
      start_date: formatDate(raw.full_certification_runs?.start_date),
      end_date: formatDate(raw.full_certification_runs?.end_date),
      studies_commencement_date: formatDate(raw.start_date),
      qualification: raw.full_certification_runs?.full_certification?.name || raw.certification_name || raw.name || '',
      // Course requirements
      course_developer: getTenantBranding().name,
      awarding_organisation: getTenantBranding().name,
      course_entry_requirements: "● With minimum 3 GCE 'O' level or International Secondary including English\n● English language proficiency: GCE 'O' level or equivalent\n● Working Experience: Matured candidate (i.e. minimum 30 years old with 8 years of working experience) with another relevant certificate qualification.",
      course_schedule: 'Refer to the attached Time Table',
      scheduled_holidays: 'PH Holidays- Refer to www.mom.gov.sg',
      examination_periods: 'Within 2 months from the each examination',
      exam_results_date: 'Within 2 months from the each examination',
      qualification_conferment_date: 'Within 2 months from the each examination',
      industrial_attachment: 'Within 2 months from the each examination',
      industrial_attachment_duration: 'NA',
      total_course_fees: formatCurrency(courseFees),
      discount: formatCurrency(discount),
      subtotal: formatCurrency(subtotal),
      gst_amount: applyGst ? formatCurrency(gstAmount) : 'N/A',
      gst_rate: applyGst ? `${orgGstSettings.gst_rate}%` : 'N/A',
      number_of_instalments: String(numInst),
      total_fees_payable: formatCurrency(totalPayable),
      // Hardcoded Misc Fees
      registration_fee: '$100.00',
      supplementary_exam_fee: '$50.00',
      re_examination_fee: '$100.00',
      re_submission_fee: '$100.00',
      medical_insurance_local: '$80.00',
      medical_insurance: '$0.00',
      fee_protection_scheme: '$0.00',
      late_payment_charges: '$100.00',
      transfer_fee: '$150.00',
      programme_deferment_fee: '$100.00',
      reprint_transcript_fee: '$100.00',
      reprint_certificate_fee: '$150.00',
      instalment_schedule: installmentschedule,
      fee_breakdown: feeBreakdown,
      contract_date: formatDate(new Date()),
      contracting_party_name: raw.participant_name || '',
      contracting_party_id: raw.nric || '',
      guardian_name: '',
      guardian_id: '',
      student_name: raw.participant_name || '',
      participant_name: raw.participant_name || '',
      student_id: raw.nric || '',
      contracting_party_signature_name: raw.participant_name || '',
      contracting_party_signature_id: raw.nric || '',
      acknowledgment_name: raw.participant_name || '',
      witness_name: '',
      witness_id: '',
      student_signature: signatureHtml,
      signature: signatureHtml,
    };
  };

  const getAllDocumentTemplates = async() => {
    const { data: documentTemplates, error: documentTemplatesError } = await supabase
      .from("document_templates")
      .select("*")
    
    if (documentTemplatesError) {
      console.error("Error fetching document templates:", documentTemplatesError);
      throw documentTemplatesError;
    }
    
    console.log("Fetched document templates:", documentTemplates);
    return documentTemplates;
  }

  // Fetch registration data
  const fetchRegistration = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("registration")
        .select(
          "*, full_certification_runs(*, full_certification(*))"
        )
        .eq("id", registrationID)
        .single();
      if (error) throw error;

      // Fetch course run details if available
      let courseRunData = null;
      let courseTypeData = null;
      let modulesData = [];
      let course_dates = [];
      
      if (data.draft_course_run_code) {
        // Fetch course run with course type details
        const { data: courseRun, error: courseRunError } = await supabase
          .from("course_run")
          .select(`
            *,
            course_type (
              code,
              name,
              description,
              course_duration,
              school,
              price
            ),
            trainers (
              name,
              email
            )
          `)
          .eq("id", data.draft_course_run_code)
          .single();

        if (!courseRunError && courseRun) {
          courseRunData = courseRun;
          courseTypeData = courseRun.course_type;
        }

        // Fetch sessions for the course run
        const { data: sessions, error: sessionsError } = await supabase
          .from("session")
          .select("*")
          .eq("course_run_code", data.draft_course_run_code)
          .order("session_date", { ascending: true });

        if (!sessionsError && sessions) {
          course_dates = sessions.map(session => ({
            date: session.session_date,
            start_time: session.start_time ? moment(session.start_time, "HH:mm:ss").format("HHmm") : null,
            end_time: session.end_time ? moment(session.end_time, "HH:mm:ss").format("HHmm") : null,
            room: session.custom_room || session.room_id,
            venue: session.custom_venue || session.venue_id,
            trainer_id: session.trainer_id,
            session_id: session.session_id,
            isCompleted: moment(session.session_date).isBefore(moment(), 'day')
          }));
        }
      }

      // If it's a full certification, fetch modules with their sessions
      // Use draft_cert_run_code or full_certification_runs.id
      const certRunCode = data.draft_cert_run_code || data.full_certification_runs?.id;
      console.log("Certification run code for modules:", certRunCode);
      
      if (certRunCode) {
        // Step 1: Get course_run_codes linked to this certification run
        const { data: linkData, error: linkError } = await supabase
          .from("full_cert_many_course_run")
          .select("course_run_code")
          .eq("cert_run_code", certRunCode);

        console.log("Link data from full_cert_many_course_run:", linkData, "Error:", linkError);

        if (!linkError && linkData && linkData.length > 0) {
          const courseRunCodes = linkData.map(l => l.course_run_code);
          console.log("Course run codes to fetch:", courseRunCodes);

          // Step 2: Fetch course_run details WITH course_type using nested select
          const { data: courseRuns, error: courseRunsError } = await supabase
            .from("course_run")
            .select(`
              id,
              course_type_code,
              start_date,
              end_date,
              venue,
              instructor_name,
              course_type (
                code,
                name,
                short_desc,
                course_duration
              )
            `)
            .in("id", courseRunCodes)
            .order("start_date", { ascending: true });

          console.log("Course runs fetched with course_type:", courseRuns, "Error:", courseRunsError);

          if (!courseRunsError && courseRuns && courseRuns.length > 0) {

            // Step 4: Fetch sessions for all course runs
            const { data: allSessions, error: sessionsError } = await supabase
              .from("session")
              .select("*")
              .in("course_run_code", courseRunCodes)
              .order("session_date", { ascending: true });

            console.log("Sessions fetched:", allSessions, "Error:", sessionsError);

            // Step 4b: Fetch venue, room, and trainer names for better display
            const venueIds = [...new Set(allSessions?.filter(s => s.venue_id).map(s => s.venue_id) || [])];
            const roomIds = [...new Set(allSessions?.filter(s => s.room_id).map(s => s.room_id) || [])];
            const trainerIds = [...new Set(allSessions?.filter(s => s.trainer_id).map(s => s.trainer_id) || [])];

            // Fetch venue names
            const venueMap = {};
            if (venueIds.length > 0) {
              const { data: venues } = await supabase
                .from("venues")
                .select("code, name, address")
                .in("code", venueIds);
              venues?.forEach(v => { venueMap[v.code] = v; });
            }

            // Fetch room names
            const roomMap = {};
            if (roomIds.length > 0) {
              const { data: rooms } = await supabase
                .from("rooms")
                .select("code, name, floor, unit")
                .in("code", roomIds);
              rooms?.forEach(r => { roomMap[r.code] = r; });
            }

            // Fetch trainer names
            const trainerMap = {};
            if (trainerIds.length > 0) {
              const { data: trainers } = await supabase
                .from("trainers")
                .select("code, name, email")
                .in("code", trainerIds);
              trainers?.forEach(t => { trainerMap[t.code] = t; });
            }

            console.log("Venue map:", venueMap, "Room map:", roomMap, "Trainer map:", trainerMap);

            // Group sessions by course_run_code with resolved names
            const sessionsByRunCode = {};
            if (allSessions) {
              allSessions.forEach(session => {
                if (!sessionsByRunCode[session.course_run_code]) {
                  sessionsByRunCode[session.course_run_code] = [];
                }
                
                // Resolve venue name
                const venueInfo = venueMap[session.venue_id];
                const venueName = session.custom_venue || venueInfo?.name || session.venue_id || 'TBC';
                
                // Resolve room name
                const roomInfo = roomMap[session.room_id];
                const roomName = session.custom_room || roomInfo?.name || session.room_id || 'TBC';
                
                // Resolve trainer name
                const trainerInfo = trainerMap[session.trainer_id];
                const trainerName = trainerInfo?.name || session.trainer_id || 'TBC';
                
                sessionsByRunCode[session.course_run_code].push({
                  id: session.id,
                  session_id: session.session_id,
                  date: session.session_date,
                  start_time: session.start_time,
                  end_time: session.end_time,
                  room: roomName,
                  room_details: roomInfo,
                  venue: venueName,
                  venue_details: venueInfo,
                  trainer_id: session.trainer_id,
                  trainer_name: trainerName,
                  trainer_details: trainerInfo,
                  isCompleted: moment(session.session_date).isBefore(moment(), 'day')
                });
              });
            }

            // Step 5: Build the modules data with course_type info (now directly from nested select) and sessions
            modulesData = courseRuns.map((cr) => {
              console.log(`Module ${cr.id}: course_type_code=${cr.course_type_code}, course_type=`, cr.course_type);
              return {
                id: cr.id,
                course_type_code: cr.course_type_code,
                start_date: cr.start_date,
                end_date: cr.end_date,
                venue: cr.venue,
                instructor_name: cr.instructor_name,
                course_type: cr.course_type || null, // Now using the nested course_type directly
                sessions: sessionsByRunCode[cr.id] || []
              };
            });
          
            console.log("Final processed modules data with sessions:", modulesData);
          }
        }
      }

      setRegistrationData({
        ...data,
        course_dates: course_dates,
        course_run: courseRunData,
        course_type: courseTypeData,
        modules: modulesData
      });
      console.log("Registration data:", data);
      console.log("Modules data:", modulesData);
      console.log("Full certification runs ID:", data.full_certification_runs?.id);
    } catch (error) {
      console.error("Error fetching registration:", error);
      toast({
        title: "Error",
        description: "Could not load registration details.",
        status: "error",
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch invoices
  const fetchInvoices = async () => {
    try {
      const { data, error } = await supabase
        .from("invoices")
        .select("*, invoice_line_items(*)")
        .eq("registration_id", registrationID)
        .order("invoice_date", { ascending: true });

      if (error) throw error;
      setInvoices(data || []);
      console.log("Invoices:", data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast({
        title: "Error",
        description: "Could not load invoices.",
        status: "error",
        isClosable: true,
        position: "top",
      });
    }
  };

  // Initialize documents from registration data (TMS program_document_templates when configured)
  const fetchAndPrepareDocuments = async (showLoading = true) => {
    if (!registrationData) return;

    try {
      const program = await resolveProgramForRegistration(supabase, registrationData);
      const { documentsList, expectedCount, allowedTemplateIds } =
        await buildPortalDocumentsList(supabase, {
          registrationIds: [registrationID],
          programType: program?.programType || null,
          programCode: program?.programCode || null,
          legacySections: DEFAULT_LEGACY_SECTIONS,
        });

      const doneCount = documentsList.filter((d) => d.status === 'done').length;
      const notStartedCount = documentsList.filter((d) => d.status === 'not_started').length;

      setDocuments(documentsList);
      setCurrentDocCount(doneCount);

      if (expectedCount > 0 && doneCount >= expectedCount) {
        setIsPolling(false);
      } else {
        setIsPolling(true);
      }

      console.log(
        `Found ${doneCount}/${expectedCount} completed documents, ${notStartedCount} not started` +
          (allowedTemplateIds ? ` (custom set: ${allowedTemplateIds.length})` : ' (legacy: all generated docs)')
      );

      return {
        hasDocuments: doneCount > 0,
        needsGeneration: notStartedCount > 0,
        expectedCount,
        allowedTemplateIds,
        doneCount,
      };
    } catch (error) {
      console.error("Error preparing documents:", error);
      if (showLoading) {
        toast({
          title: "Error loading documents",
          description: "Could not load documents.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      }
      return false;
    }
  };

  // Check if document generation is complete
  const checkGenerationComplete = async () => {
    try {
      const program = await resolveProgramForRegistration(supabase, registrationData);
      const { expectedCount, allowedTemplateIds } = await buildPortalDocumentsList(supabase, {
        registrationIds: [registrationID],
        programType: program?.programType || null,
        programCode: program?.programCode || null,
        legacySections: DEFAULT_LEGACY_SECTIONS,
      });

      const { data: generatedDocs } = await supabase
        .from('document_generator_docs')
        .select('id, template_id, pdf_file_id')
        .eq('registration_id', registrationID);

      const currentCount = countCompletedPortalDocs(generatedDocs, allowedTemplateIds);
      return expectedCount > 0 && currentCount >= expectedCount;
    } catch (error) {
      console.error("Error checking generation completion:", error);
      return false;
    }
  };

  // Trigger PDF generation for all missing templates
  const triggerPDFGenerationForAll = async () => {
    if (!registrationData || !registrationID) return false;

    try {
      console.log(`Triggering PDF generation for all templates for registration ${registrationID}`);

      // copy-google-doc resolves the default doc-gen Google account server-side
      const { data, error } = await supabase.functions.invoke('copy-google-doc', {
        body: {
          registrationIds: [registrationID],
        },
      });

      if (error) {
        console.error('Error triggering PDF generation:', error);
        return false;
      } else {
        console.log('PDF generation job created successfully:', data);
        return true;
      }
    } catch (error) {
      console.error('Error in triggerPDFGenerationForAll:', error);
      return false;
    }
  };

  // Trigger PDF generation if payment received but no job exists
  const triggerPDFGenerationIfNeeded = async () => {
    if (!registrationData || !registrationID) return;

    // Prevent duplicate triggers
    if (hasTriggeredGenerationRef.current) {
      console.log('PDF generation already triggered for this registration, skipping');
      return;
    }

    try {
      // Check if payment has been received
      if (registrationData.registration_stage !== 'payment_received' && registrationData.registration_stage !== 'payment_in_progress') {
        console.log('Payment not received yet, skipping PDF generation trigger');
        return;
      }

      // Check if there are already generated documents for this registration
      const { data: existingDocs } = await supabase
        .from('document_generator_docs')
        .select('id')
        .eq('registration_id', registrationID)
        .not('pdf_file_id', 'is', null)
        .limit(1);

      // If documents already exist, no need to trigger
      if (existingDocs && existingDocs.length > 0) {
        console.log('Documents already exist for this registration, skipping trigger');
        hasTriggeredGenerationRef.current = true; // Mark as triggered to prevent future checks
        return;
      }

      console.log(`Triggering PDF generation for registration ${registrationID} (payment received but no documents found)`);

      // copy-google-doc resolves the default doc-gen Google account server-side
      const { data, error } = await supabase.functions.invoke('copy-google-doc', {
        body: {
          registrationIds: [registrationID],
        },
      });

      // Mark as triggered to prevent duplicate calls
      hasTriggeredGenerationRef.current = true;

      if (error) {
        console.error('Error triggering PDF generation:', error);
        toast({
          title: "PDF Generation Started",
          description: "Documents are being generated in the background. This page will update automatically.",
          status: "info",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      } else {
        console.log('PDF generation job created successfully:', data);
        toast({
          title: "PDF Generation Started",
          description: "Your documents are being generated. This page will update automatically when ready.",
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      }
    } catch (error) {
      console.error('Error in triggerPDFGenerationIfNeeded:', error);
      // Reset trigger flag on error so it can be retried
      hasTriggeredGenerationRef.current = false;
      // Don't show error toast - just log it, as this is a background operation
    }
  };

  useEffect(() => {
    if (!registrationData || !registrationID) {
      // Clear polling if no registration data
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setIsPolling(false);
      return;
    }

    // Clear any existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // Initialize: fetch documents first
    const initializeDocuments = async () => {
      const result = await fetchAndPrepareDocuments();
      
      // If there are templates that need generation, trigger it automatically
      if (result?.needsGeneration && !hasTriggeredGenerationRef.current) {
        console.log('Found templates that need generation, triggering automatically...');
        const triggered = await triggerPDFGenerationForAll();
        if (triggered) {
          hasTriggeredGenerationRef.current = true; // Prevent duplicate triggers
          toast({
            title: "Document Generation Started",
            description: "All documents are being generated. This page will update automatically as they complete.",
            status: "info",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
          // Small delay then refresh to show generating status
          setTimeout(() => {
            fetchAndPrepareDocuments(false);
          }, 1000);
        }
      }
      
      // If documents found, check if generation is complete
      if (result?.hasDocuments) {
        const isComplete = await checkGenerationComplete();
        if (isComplete) {
          setIsPolling(false);
          return; // Don't start polling if complete
        }
      }
      
      // Start polling if documents not found or generation not complete
      setIsPolling(true);
      setCurrentDocCount(0);
      
      let pollCount = 0;
      const maxPolls = 60; // Poll for up to 3 minutes (60 * 3 seconds)
      let lastDocCount = 0;
      let stableCount = 0; // Track consecutive polls with same count
      
      pollIntervalRef.current = setInterval(async () => {
        pollCount++;
        
        // Refresh documents (don't show loading spinner during polling)
        const result = await fetchAndPrepareDocuments(false);

        const expectedCount = result?.expectedCount || 0;
        const completedCount = result?.doneCount || 0;
        setCurrentDocCount(completedCount);
        
        // If all expected program docs are done, stop polling
        if (expectedCount > 0 && completedCount >= expectedCount) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setIsPolling(false);
          return;
        }
        
        // If document count hasn't changed, increment stable count
        if (completedCount === lastDocCount && completedCount > 0) {
          stableCount++;
          
          // If count hasn't changed for 3 consecutive polls (9 seconds), stop polling
          if (stableCount >= 3) {
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            setIsPolling(false);
            return;
          }
        } else {
          // Count changed, reset stable count and update last count
          stableCount = 0;
          lastDocCount = completedCount;
        }
        
        // Stop polling after max polls
        if (pollCount >= maxPolls) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setIsPolling(false);
          // Final refresh
          await fetchAndPrepareDocuments(false);
        }
      }, 3000); // Poll every 3 seconds (same as studentpackage.tsx)
    };
    
    initializeDocuments();
    
    // Cleanup polling on unmount or when registration changes
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setIsPolling(false);
    };
  }, [registrationData, registrationID]);

  useEffect(() => {
    fetchRegistration();
    fetchInvoices();
    // Reset trigger flag when registration ID changes
    hasTriggeredGenerationRef.current = false;
  }, [registrationID]);
  
  // Also reset trigger flag when registration data changes (e.g., payment received)
  useEffect(() => {
    if (registrationData?.registration_stage === 'payment_received' || registrationData?.registration_stage === 'payment_in_progress') {
      // Reset trigger flag when payment is received to allow auto-generation
      hasTriggeredGenerationRef.current = false;
    }
  }, [registrationData?.registration_stage]);

  // Trigger PDF generation if payment received but no documents exist
  useEffect(() => {
    if (!registrationData || !registrationID) return;

    // Only trigger if payment has been received
    if (registrationData.registration_stage === 'payment_received' || registrationData.registration_stage === 'payment_in_progress') {
      // Small delay to ensure registration data is fully loaded and avoid duplicate triggers
      const timer = setTimeout(() => {
        triggerPDFGenerationIfNeeded();
      }, 1500);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationData?.registration_stage, registrationID]);

  // Helper function to create printable HTML with proper CSS for page breaks
  const createPrintableHTML = (htmlContent, title) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${title}</title>
        
      </head>
      <body>
        ${htmlContent}
      </body>
      </html>
    `;
  };

  const viewDocument = (doc) => {
    // Set active document and open modal
    // For PDFs, we'll try iframe first, with option to open in new tab
    setActiveDocument(doc);
    onDocOpen();
  };

  // Download individual document as PDF
  const downloadDocumentAsPdf = async (doc, suppressToast = false) => {
    setDownloadingDoc(doc.title);
    try {
      // If it's a PDF from Google Drive, download directly without opening new tab
      if (doc.isPdf && doc.pdfDownloadUrl) {
        // Create a temporary link element and trigger download
        const link = document.createElement('a');
        link.href = doc.pdfDownloadUrl;
        link.download = `${doc.title}.pdf`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Mark as downloaded
        setDownloadedDocs(prev => new Set([...prev, doc.id || doc.title]));
        
        if (!suppressToast) {
          toast({
            title: "Download Started",
            description: `Downloading ${doc.title}...`,
            status: "success",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
        }
      } else {
        // For HTML documents, use print dialog
        const printableHTML = createPrintableHTML(doc.html, doc.title);
        
        // Create a hidden iframe
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        
        // Write the HTML to the iframe
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(printableHTML);
        iframeDoc.close();
        
        // Wait for content to load
        await new Promise(resolve => {
          if (iframe.contentWindow) {
            iframe.contentWindow.onload = resolve;
          } else {
            setTimeout(resolve, 500);
          }
        });
        
        // Trigger print dialog
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        
        // Clean up after a delay
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);

        toast({
          title: "Print Dialog Opened",
          description: `Choose "Save as PDF" to download ${doc.title}.`,
          status: "info",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      }
    } catch (error) {
      console.error("Error downloading document:", error);
      toast({
        title: "Download Error",
        description: "Failed to download document. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setDownloadingDoc(null);
    }
  };

  // Download all completed PDFs by simulating clicks on each download button
  const downloadAllDocumentsAsPdf = async () => {
    setDownloadingAll(true);
    try {
      // Filter only completed PDFs that haven't been downloaded yet
      const completedPdfs = documents.filter(
        doc => doc.status === 'done' && 
        doc.isPdf && 
        doc.pdfDownloadUrl &&
        !downloadedDocs.has(doc.id || doc.title)
      );
      
      if (completedPdfs.length === 0) {
        toast({
          title: "No PDFs Available",
          description: "No new PDFs to download. All PDFs have already been downloaded.",
          status: "info",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        setDownloadingAll(false);
        return;
      }

      // Simulate clicking each download button one by one with 2.5 second delays
      for (let i = 0; i < completedPdfs.length; i++) {
        const doc = completedPdfs[i];
        
        // Wait before each download (except the first one)
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 2500)); // 2.5 second delay between downloads
        }
        
        // Simulate clicking the download button by calling the download function
        // Suppress individual toasts during bulk download
        await downloadDocumentAsPdf(doc, true);
      }

      toast({
        title: "All Downloads Started",
        description: `Started downloading ${completedPdfs.length} PDF file(s)...`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } catch (error) {
      console.error("Error downloading PDFs:", error);
      toast({
        title: "Download Error",
        description: "Failed to download PDFs. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setDownloadingAll(false);
    }
  };

  if (loading) {
    return (
      <Center h="100vh" bg="gray.50">
        <Spinner size="xl" thickness="4px" speed="0.65s" color="brand.600" />
      </Center>
    );
  }

  if (!registrationData) {
    return (
      <Center h="100vh" bg="gray.50">
        <VStack spacing={4}>
          <Text fontSize="lg" fontWeight="semibold">No registration data found.</Text>
          <Button onClick={() => navigate('/')}>Return to Home</Button>
        </VStack>
      </Center>
    );
  }

  return (
    <>
      <Container
        w={"100%"}
        maxW="1400px"
        display="flex"
        justifyContent="center"
        alignItems="flex-start"
        bg="gray.50"
        minH="100vh"
        py={{ base: 4, md: 8 }}
        px={{ base: 2, md: 4 }}
      >
        <Box
          width="100%"
          bg="white"
          px={{ base: 4, md: 10 }}
          py={{ base: 6, md: 10 }}
          borderRadius="lg"
          boxShadow="md"
        >
          <Heading
            size={{ base: "xs", md: "sm" }}
            mb={{ base: 4, md: 6 }}
            color="brand.700"
            borderBottom="2px solid"
            borderColor="brand.400"
            pb={2}
            width="fit-content"
          >
            Student Package
          </Heading>

          {/* Payment Success Alert */}
          {showPaymentSuccess && (
            <Alert 
              status="success" 
              mb={4}
              borderRadius="lg"
              bg="green.50"
              border="1px solid"
              borderColor="green.200"
              flexDirection={{ base: "column", md: "row" }}
            >
              <AlertIcon color="green.500" />
              <VStack align="start" spacing={1} flex={1}>
                <Text fontWeight="bold" color="green.700" fontSize={{ base: "sm", md: "md" }}>Payment Received Successfully!</Text>
                <Text fontSize={{ base: "xs", md: "sm" }} color="green.600">
                  Thank you for your payment. Your registration has been confirmed and your enrollment documents are now being generated.
                </Text>
              </VStack>
              <Button 
                size="sm" 
                variant="ghost" 
                colorScheme="green"
                ml={{ base: 0, md: "auto" }}
                mt={{ base: 2, md: 0 }}
                alignSelf={{ base: "flex-end", md: "auto" }}
                onClick={() => setShowPaymentSuccess(false)}
              >
                Dismiss
              </Button>
            </Alert>
          )}

          <Alert status="success" mb={{ base: 6, md: 8 }}>
            <AlertIcon />
            <VStack align="start" spacing={1}>
              <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>Welcome, {registrationData.participant_name}!</Text>
              <Text fontSize={{ base: "xs", md: "sm" }}>
                Below you'll find all your enrollment documents, invoices, and course information.
              </Text>
            </VStack>
          </Alert>

          {/* Course Information Section - Redesigned */}
          <Box mb={8}>
            {/* Header with Progress Overview */}
            <Flex 
              justify="space-between" 
              align={{ base: "flex-start", md: "center" }}
              mb={{ base: 4, md: 6 }}
              flexWrap="wrap"
              gap={4}
              flexDirection={{ base: "column", md: "row" }}
            >
              <Heading size={{ base: "sm", md: "md" }} color="brand.700" display="flex" alignItems="center" gap={2}>
                <Icon as={FaGraduationCap} />
                Course Information
              </Heading>
              
              {/* Progress Overview for Full Certification */}
              {registrationData.modules && registrationData.modules.length > 0 && (
                <HStack spacing={2} flexWrap="wrap">
                  <Badge colorScheme="green" px={{ base: 2, md: 3 }} py={1} borderRadius="full" fontSize={{ base: "xs", md: "sm" }}>
                    <HStack spacing={1}>
                      <Icon as={FaCheckCircle} boxSize={{ base: 3, md: 4 }} />
                      <Text>{registrationData.modules.filter(m => m.end_date && moment(m.end_date).isBefore(moment(), 'day')).length} Completed</Text>
                    </HStack>
                  </Badge>
                  <Badge colorScheme="blue" px={{ base: 2, md: 3 }} py={1} borderRadius="full" fontSize={{ base: "xs", md: "sm" }}>
                    <HStack spacing={1}>
                      <Icon as={FaHourglassHalf} boxSize={{ base: 3, md: 4 }} />
                      <Text display={{ base: "none", sm: "block" }}>{registrationData.modules.filter(m => m.start_date && moment(m.start_date).isSameOrBefore(moment(), 'day') && m.end_date && moment(m.end_date).isSameOrAfter(moment(), 'day')).length} In Progress</Text>
                      <Text display={{ base: "block", sm: "none" }}>{registrationData.modules.filter(m => m.start_date && moment(m.start_date).isSameOrBefore(moment(), 'day') && m.end_date && moment(m.end_date).isSameOrAfter(moment(), 'day')).length} Progress</Text>
                    </HStack>
                  </Badge>
                  <Badge colorScheme="gray" px={{ base: 2, md: 3 }} py={1} borderRadius="full" fontSize={{ base: "xs", md: "sm" }}>
                    {registrationData.modules.filter(m => !m.start_date || moment(m.start_date).isAfter(moment(), 'day')).length} Upcoming
                  </Badge>
                </HStack>
              )}
            </Flex>
            
            {/* Main Course Header Card */}
            <Box 
              mb={{ base: 4, md: 6 }} 
              p={{ base: 4, md: 6 }} 
              bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              borderRadius="xl" 
              color="white"
              boxShadow="lg"
            >
              <VStack align="start" spacing={3}>
                <Heading size={{ base: "md", md: "lg" }}>
                  {registrationData.name || 
                   registrationData.full_certification_runs?.full_certification?.name ||
                   registrationData.course_type?.name ||
                   "Course"}
                </Heading>
                {(registrationData.full_certification_runs?.full_certification?.description || 
                  registrationData.course_type?.description) && (
                  <Text fontSize={{ base: "xs", md: "sm" }} opacity={0.9}>
                    {registrationData.full_certification_runs?.full_certification?.description ||
                     registrationData.course_type?.description}
                  </Text>
                )}
                {registrationData.full_certification_runs?.run_name && (
                  <Badge 
                    bg="whiteAlpha.300" 
                    color="white" 
                    px={{ base: 2, md: 3 }} 
                    py={1} 
                    borderRadius="full"
                    fontSize={{ base: "xs", md: "sm" }}
                  >
                    {registrationData.full_certification_runs.run_name}
                  </Badge>
                )}
              </VStack>
            </Box>

            {/* Quick Stats Row */}
            <Box 
              display="grid" 
              gridTemplateColumns={{ base: "1fr 1fr", md: "repeat(4, 1fr)" }}
              gap={{ base: 3, md: 4 }}
              mb={{ base: 4, md: 6 }}
            >
              {/* Start Date */}
              {(registrationData.full_certification_runs?.start_date || 
                registrationData.course_run?.start_date || 
                registrationData.start_date) && (
                <Box 
                  p={{ base: 3, md: 4 }} 
                  bg="white" 
                  borderRadius="xl" 
                  border="1px solid"
                  borderColor="gray.100"
                  boxShadow="sm"
                  textAlign="center"
                >
                  <Icon as={FaCalendarAlt} color="blue.500" boxSize={{ base: 4, md: 5 }} mb={2} />
                  <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500" textTransform="uppercase" fontWeight="bold">Start Date</Text>
                  <Text fontSize={{ base: "sm", md: "lg" }} fontWeight="bold" color="gray.800">
                    {moment(
                      registrationData.full_certification_runs?.start_date || 
                      registrationData.course_run?.start_date || 
                      registrationData.start_date
                    ).format("DD MMM YYYY")}
                  </Text>
                </Box>
              )}

              {/* End Date */}
              {(registrationData.full_certification_runs?.end_date || 
                registrationData.course_run?.end_date || 
                registrationData.end_date) && (
                <Box 
                  p={{ base: 3, md: 4 }} 
                  bg="white" 
                  borderRadius="xl" 
                  border="1px solid"
                  borderColor="gray.100"
                  boxShadow="sm"
                  textAlign="center"
                >
                  <Icon as={FaCalendarAlt} color="green.500" boxSize={{ base: 4, md: 5 }} mb={2} />
                  <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500" textTransform="uppercase" fontWeight="bold">End Date</Text>
                  <Text fontSize={{ base: "sm", md: "lg" }} fontWeight="bold" color="gray.800">
                    {moment(
                      registrationData.full_certification_runs?.end_date || 
                      registrationData.course_run?.end_date || 
                      registrationData.end_date
                    ).format("DD MMM YYYY")}
                  </Text>
                </Box>
              )}

              {/* Duration */}
              {(registrationData.full_certification_runs?.full_certification?.duration || 
                registrationData.course_type?.course_duration) && (
                <Box 
                  p={{ base: 3, md: 4 }} 
                  bg="white" 
                  borderRadius="xl" 
                  border="1px solid"
                  borderColor="gray.100"
                  boxShadow="sm"
                  textAlign="center"
                >
                  <Icon as={FaClock} color="purple.500" boxSize={{ base: 4, md: 5 }} mb={2} />
                  <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500" textTransform="uppercase" fontWeight="bold">Duration</Text>
                  <Text fontSize={{ base: "sm", md: "lg" }} fontWeight="bold" color="gray.800">
                    {registrationData.full_certification_runs?.full_certification?.duration 
                      ? `${registrationData.full_certification_runs.full_certification.duration} Months`
                      : registrationData.course_type?.course_duration || "N/A"}
                  </Text>
                </Box>
              )}

              {/* Total Modules */}
              {registrationData.modules && registrationData.modules.length > 0 && (
                <Box 
                  p={{ base: 3, md: 4 }} 
                  bg="white" 
                  borderRadius="xl" 
                  border="1px solid"
                  borderColor="gray.100"
                  boxShadow="sm"
                  textAlign="center"
                >
                  <Icon as={FaBook} color="orange.500" boxSize={{ base: 4, md: 5 }} mb={2} />
                  <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500" textTransform="uppercase" fontWeight="bold">Modules</Text>
                  <Text fontSize={{ base: "sm", md: "lg" }} fontWeight="bold" color="gray.800">
                    {registrationData.modules.length} Total
                  </Text>
                </Box>
              )}
            </Box>

            {/* Course Schedule Table (for single courses) */}
            {registrationData.course_dates && registrationData.course_dates.length > 0 && !registrationData.modules?.length && (
              <Box 
                mb={{ base: 4, md: 6 }} 
                p={{ base: 3, md: 5 }} 
                bg="white" 
                borderRadius="xl" 
                border="1px solid"
                borderColor="gray.100"
                boxShadow="sm"
              >
                <Heading size={{ base: "xs", md: "sm" }} color="brand.700" mb={{ base: 3, md: 4 }} display="flex" alignItems="center" gap={2}>
                  <Icon as={FaCalendarAlt} />
                  Training Schedule
                </Heading>
                <Box overflowX="auto" w="100%">
                  <Table variant="simple" size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>Status</Th>
                        <Th>Date</Th>
                        <Th>Time</Th>
                        {registrationData.course_dates.some(s => s.venue) && <Th>Venue</Th>}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {registrationData.course_dates.map((session, index) => {
                        const isCompleted = session.isCompleted;
                        const isToday = moment(session.date).isSame(moment(), 'day');
                        return (
                          <Tr 
                            key={index} 
                            bg={isCompleted ? "gray.50" : isToday ? "blue.50" : "white"}
                            _hover={{ bg: "gray.50" }}
                          >
                            <Td>
                              {isCompleted ? (
                                <Badge colorScheme="gray" fontSize="xs">Completed</Badge>
                              ) : isToday ? (
                                <Badge colorScheme="blue" fontSize="xs">Today</Badge>
                              ) : (
                                <Badge colorScheme="green" fontSize="xs">Upcoming</Badge>
                              )}
                            </Td>
                            <Td>
                              <Text fontWeight="semibold">{moment(session.date).format("DD MMM YYYY")}</Text>
                              <Text fontSize="xs" color="gray.500">{moment(session.date).format("dddd")}</Text>
                            </Td>
                            <Td>
                              {session.start_time && session.end_time 
                                ? `${moment(session.start_time, "HH:mm:ss").format("h:mm A")} - ${moment(session.end_time, "HH:mm:ss").format("h:mm A")}`
                                : "-"}
                            </Td>
                            {registrationData.course_dates.some(s => s.venue) && (
                              <Td color="gray.600">{session.venue || "-"}</Td>
                            )}
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            )}

            {/* Modules Section - Expandable Accordion Style */}
            {registrationData.modules && registrationData.modules.length > 0 ? (
              <Box 
                bg="white" 
                borderRadius="xl" 
                border="1px solid"
                borderColor="gray.100"
                boxShadow="sm"
                overflow="hidden"
              >
                <Box p={{ base: 3, md: 4 }} bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
                  <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} wrap="wrap" gap={3} flexDirection={{ base: "column", sm: "row" }}>
                    <HStack>
                      <Heading size={{ base: "xs", md: "sm" }} color="brand.700" display="flex" alignItems="center" gap={2}>
                        <Icon as={FaBook} />
                        Course Modules ({registrationData.modules.length})
                      </Heading>
                      <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500" display={{ base: "none", md: "block" }}>
                        - Click to expand
                      </Text>
                    </HStack>
                    <Button
                      size={{ base: "xs", md: "sm" }}
                      colorScheme="teal"
                      variant="outline"
                      leftIcon={<Icon as={FaCalendarPlus} />}
                      onClick={() => handleDownloadAllModulesCalendar(
                        registrationData.modules, 
                        registrationData.full_certification_runs?.full_certification?.name || registrationData.course_type?.name
                      )}
                      w={{ base: "100%", sm: "auto" }}
                      sx={{ fontWeight: 500, letterSpacing: "normal" }}
                    >
                      <Box as="span" display={{ base: "none", sm: "inline" }}>Add All Sessions to Calendar</Box>
                      <Box as="span" display={{ base: "inline", sm: "none" }}>Add All to Calendar</Box>
                    </Button>
                  </Flex>
                </Box>
                
                <VStack spacing={0} align="stretch" divider={<Divider />}>
                  {registrationData.modules.map((module, index) => {
                    const isModuleCompleted = module.end_date && moment(module.end_date).isBefore(moment(), 'day');
                    const isModuleInProgress = module.start_date && 
                      moment(module.start_date).isSameOrBefore(moment(), 'day') && 
                      module.end_date && 
                      moment(module.end_date).isSameOrAfter(moment(), 'day');
                    const moduleName = module.course_type?.name || module.course_type_code || `Module ${index + 1}`;
                    const isExpanded = expandedModules.has(module.id);
                    const completedSessions = module.sessions?.filter(s => s.isCompleted).length || 0;
                    const totalSessions = module.sessions?.length || 0;
                    
                    return (
                      <Box key={module.id || index}>
                        {/* Module Header Row - Clickable */}
                        <Box
                          p={{ base: 3, md: 4 }}
                          cursor="pointer"
                          onClick={() => toggleModuleExpansion(module.id)}
                          bg={isModuleCompleted ? "green.50" : isModuleInProgress ? "blue.50" : "white"}
                          _hover={{ bg: isModuleCompleted ? "green.100" : isModuleInProgress ? "blue.100" : "gray.50" }}
                          transition="all 0.2s"
                          borderLeft="4px solid"
                          borderColor={isModuleCompleted ? "green.400" : isModuleInProgress ? "blue.400" : "gray.200"}
                        >
                          <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} flexDirection={{ base: "column", md: "row" }} gap={{ base: 2, md: 0 }}>
                            <HStack spacing={{ base: 2, md: 4 }} flex={1} w="100%">
                              {/* Module Number */}
                              <Box
                                w={{ base: 6, md: 8 }}
                                h={{ base: 6, md: 8 }}
                                borderRadius="full"
                                bg={isModuleCompleted ? "green.500" : isModuleInProgress ? "blue.500" : "gray.300"}
                                color="white"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                fontWeight="bold"
                                fontSize={{ base: "xs", md: "sm" }}
                                flexShrink={0}
                              >
                                {isModuleCompleted ? <CheckCircleIcon boxSize={{ base: 3, md: 4 }} /> : index + 1}
                              </Box>
                              
                              {/* Module Info */}
                              <VStack align="start" spacing={0} flex={1} minW={0}>
                                <Text fontWeight="semibold" color="gray.800" fontSize={{ base: "sm", md: "md" }} noOfLines={2}>
                                  {moduleName}
                                </Text>
                                <HStack spacing={{ base: 2, md: 4 }} mt={1} flexWrap="wrap">
                                  <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500">
                                    <Icon as={FaCalendarAlt} mr={1} boxSize={{ base: 2, md: 3 }} />
                                    {module.start_date ? moment(module.start_date).format("DD MMM") : "TBC"} - {module.end_date ? moment(module.end_date).format("DD MMM YYYY") : "TBC"}
                                  </Text>
                                  {totalSessions > 0 && (
                                    <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500">
                                      <Icon as={FaChalkboardTeacher} mr={1} boxSize={{ base: 2, md: 3 }} />
                                      {completedSessions}/{totalSessions} sessions
                                    </Text>
                                  )}
                                </HStack>
                              </VStack>
                            </HStack>
                            
                            {/* Status Badge and Expand Icon */}
                            <HStack spacing={2} alignSelf={{ base: "flex-end", md: "auto" }} mt={{ base: 2, md: 0 }}>
                              <Badge 
                                colorScheme={isModuleCompleted ? "green" : isModuleInProgress ? "blue" : "gray"}
                                px={{ base: 2, md: 3 }}
                                py={1}
                                borderRadius="full"
                                fontSize={{ base: "xs", md: "sm" }}
                              >
                                {isModuleCompleted ? "Completed" : isModuleInProgress ? "In Progress" : "Upcoming"}
                              </Badge>
                              <Icon 
                                as={isExpanded ? ChevronUpIcon : ChevronDownIcon} 
                                color="gray.500"
                                boxSize={{ base: 4, md: 5 }}
                              />
                            </HStack>
                          </Flex>
                        </Box>
                        
                        {/* Expanded Sessions Content */}
                        {isExpanded && (
                          <Box 
                            bg="gray.50" 
                            px={{ base: 3, md: 6 }} 
                            py={{ base: 3, md: 4 }}
                            borderTop="1px solid"
                            borderColor="gray.200"
                          >
                            {module.sessions && module.sessions.length > 0 ? (
                              <VStack spacing={3} align="stretch">
                                {/* Add All to Calendar Button */}
                                <Flex justify="flex-end">
                                  <Button
                                    size={{ base: "xs", md: "sm" }}
                                    colorScheme="purple"
                                    variant="outline"
                                    leftIcon={<Icon as={FaCalendarPlus} />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadModuleCalendar(module, registrationData.full_certification_runs?.full_certification?.name || registrationData.course_type?.name);
                                    }}
                                    w={{ base: "100%", sm: "auto" }}
                                  >
                                    <Text display={{ base: "none", sm: "block" }}>Add All {module.sessions.length} Sessions to Calendar</Text>
                                    <Text display={{ base: "block", sm: "none" }}>Add All to Calendar</Text>
                                  </Button>
                                </Flex>
                                <Box overflowX="auto" w="100%">
                                  <Table variant="simple" size="sm" bg="white" borderRadius="md">
                                    <Thead bg="gray.100">
                                      <Tr>
                                        <Th width="90px">Status</Th>
                                        <Th>Date</Th>
                                        <Th>Time</Th>
                                        <Th>Venue</Th>
                                        <Th>Room</Th>
                                        <Th>Trainer</Th>
                                        <Th width="50px" textAlign="center">
                                          <Icon as={FaCalendarPlus} color="gray.600" />
                                        </Th>
                                      </Tr>
                                    </Thead>
                                    <Tbody>
                                      {module.sessions.map((session, sIndex) => {
                                        const isSessionCompleted = session.isCompleted;
                                        const isSessionToday = moment(session.date).isSame(moment(), 'day');
                                        return (
                                          <Tr 
                                            key={sIndex}
                                            bg={isSessionCompleted ? "gray.50" : isSessionToday ? "blue.50" : "white"}
                                          >
                                            <Td>
                                              {isSessionCompleted ? (
                                                <Badge colorScheme="gray" size="sm">
                                                  <HStack spacing={1}>
                                                    <CheckCircleIcon />
                                                    <Text>Done</Text>
                                                  </HStack>
                                                </Badge>
                                              ) : isSessionToday ? (
                                                <Badge colorScheme="blue" size="sm">Today</Badge>
                                              ) : (
                                                <Badge colorScheme="green" size="sm">Upcoming</Badge>
                                              )}
                                            </Td>
                                            <Td>
                                              <Text fontWeight="medium">
                                                {moment(session.date).format("DD MMM YYYY")}
                                              </Text>
                                              <Text fontSize="xs" color="gray.500">
                                                {moment(session.date).format("dddd")}
                                              </Text>
                                            </Td>
                                            <Td>
                                              {session.start_time && session.end_time ? (
                                                <Text fontSize="sm">
                                                  {moment(session.start_time, "HH:mm:ss").format("h:mm A")} - {moment(session.end_time, "HH:mm:ss").format("h:mm A")}
                                                </Text>
                                              ) : (
                                                <Text color="gray.400">TBC</Text>
                                              )}
                                            </Td>
                                            <Td>
                                              <Text fontSize="sm" color="gray.700">{session.venue || "-"}</Text>
                                              {session.venue_details?.address && (
                                                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                                  {session.venue_details.address}
                                                </Text>
                                              )}
                                            </Td>
                                            <Td>
                                              <Text fontSize="sm" color="gray.700">{session.room || "-"}</Text>
                                              {session.room_details?.floor && (
                                                <Text fontSize="xs" color="gray.500">
                                                  Level {session.room_details.floor}
                                                  {session.room_details.unit && `, Unit ${session.room_details.unit}`}
                                                </Text>
                                              )}
                                            </Td>
                                            <Td>
                                              <HStack spacing={1}>
                                                <Icon as={FaUser} color="gray.400" boxSize={3} />
                                                <Text fontSize="sm" color="gray.700">{session.trainer_name || "-"}</Text>
                                              </HStack>
                                            </Td>
                                            <Td textAlign="center">
                                              <Button
                                                size="xs"
                                                colorScheme="purple"
                                                variant="ghost"
                                                title="Add to Calendar"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDownloadSessionCalendar(
                                                    session, 
                                                    moduleName, 
                                                    registrationData.full_certification_runs?.full_certification?.name || registrationData.course_type?.name
                                                  );
                                                }}
                                              >
                                                <Icon as={FaCalendarPlus} />
                                              </Button>
                                            </Td>
                                          </Tr>
                                        );
                                      })}
                                    </Tbody>
                                  </Table>
                                </Box>
                              </VStack>
                            ) : (
                              <Box textAlign="center" py={4}>
                                <Text color="gray.500" fontSize="sm">
                                  <Icon as={FaInfoCircle} mr={2} />
                                  Session schedule will be available soon.
                                </Text>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </VStack>
              </Box>
            ) : registrationData.draft_cert_run_code && (
              <Box p={4} bg="yellow.50" borderRadius="lg" border="1px solid" borderColor="yellow.200">
                <HStack spacing={2}>
                  <Icon as={FaInfoCircle} color="yellow.600" />
                  <Text color="yellow.800" fontSize="sm">
                    Module schedule will be available soon. Please check back later.
                  </Text>
                </HStack>
              </Box>
            )}
          </Box>

          {/* Invoices Section */}
          {invoices.length > 0 && (() => {
            const visibleInvoices = invoices.filter((inv) => !isSupersededInvoice(inv));
            if (visibleInvoices.length === 0) return null;
            const unpaidWithDueDate = visibleInvoices.filter((inv) => {
              const paid = inv.status === "paid" || inv.status === "Paid";
              if (paid) return false;
              const due = moment(inv.due_date).startOf("day");
              return inv.due_date && due.isValid();
            });
            const certName = registrationData?.full_certification_runs?.full_certification?.name || registrationData?.name || "Course";
            return (
            <Box mb={{ base: 6, md: 8 }} p={{ base: 3, md: 6 }} bg="gray.50" borderRadius="lg" border="1px solid" borderColor="gray.200">
              <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} mb={{ base: 3, md: 4 }} flexDirection={{ base: "column", md: "row" }} gap={3}>
                <Heading size={{ base: "xs", md: "sm" }} color="brand.700">
                  Invoices
                </Heading>
                {unpaidWithDueDate.length > 0 && (
                  <Button
                    size={{ base: "xs", md: "sm" }}
                    colorScheme="purple"
                    variant="outline"
                    leftIcon={<Icon as={FaCalendarPlus} />}
                    onClick={() => handleDownloadInvoiceDueDatesCalendar(visibleInvoices, certName)}
                    w={{ base: "100%", md: "auto" }}
                  >
                    Add due dates to calendar
                  </Button>
                )}
              </Flex>
              
              {/* Mobile Card View */}
              <VStack spacing={2} align="stretch" display={{ base: "flex", md: "none" }}>
                {visibleInvoices.map((invoice) => {
                  const isPaid = invoice.status === 'paid' || invoice.status === 'Paid';
                  const subtotalAmount = invoice.invoice_line_items && invoice.invoice_line_items.length > 0
                    ? invoice.invoice_line_items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0)
                    : 0;
                  const gstAmount = invoice.gst_amount || 0;
                  const totalAmount = subtotalAmount + gstAmount;
                  const dueInBadge = getDueInBadge(invoice);
                  return (
                    <Box
                      key={invoice.id}
                      bg="white"
                      p={3}
                      borderRadius="md"
                      border="1px solid"
                      borderColor={isPaid ? "green.200" : "gray.200"}
                      borderLeft={isPaid ? "4px solid" : "1px solid"}
                      borderLeftColor={isPaid ? "green.400" : "gray.200"}
                      boxShadow="sm"
                    >
                      <Flex justify="space-between" align="center" mb={2}>
                        <HStack spacing={2} flex={1}>
                          <Text fontWeight="bold" fontSize="sm" color="gray.800">
                            {invoice.invoice_type}
                          </Text>
                          <Badge colorScheme={isPaid ? 'green' : 'orange'} fontSize="2xs" px={1.5} py={0.5}>
                            {invoice.status}
                          </Badge>
                        </HStack>
                        <Text fontWeight="bold" fontSize="md" color={isPaid ? "green.600" : "gray.800"}>
                          {formatCurrency(totalAmount)}
                        </Text>
                      </Flex>
                      
                      <VStack align="stretch" spacing={1.5}>
                        <Flex justify="space-between" align="center">
                          <Text fontSize="2xs" color="gray.500" fontWeight="medium">Invoice Date</Text>
                          <Text fontSize="xs" color="gray.700" fontWeight={isPaid ? "semibold" : "normal"}>
                            {formatInvoiceDate(invoice.invoice_date)}
                          </Text>
                        </Flex>
                        <Flex justify="space-between" align="center">
                          <Text fontSize="2xs" color="gray.500" fontWeight="medium">Due Date</Text>
                          <Text fontSize="xs" color="gray.700" fontWeight={isPaid ? "semibold" : "normal"}>
                            {formatInvoiceDate(invoice.due_date)}
                          </Text>
                        </Flex>
                        <Flex justify="space-between" align="center">
                          <Text fontSize="2xs" color="gray.500" fontWeight="medium">Due in</Text>
                          {dueInBadge ? <Badge bg={dueInBadge.bg} color={dueInBadge.color} fontSize="2xs" px={2} py={0.5}>{dueInBadge.label}</Badge> : <Text fontSize="xs" color="gray.500">—</Text>}
                        </Flex>
                        <Flex justify="space-between" align="center">
                          <Text fontSize="2xs" color="gray.500" fontWeight="medium">Subtotal</Text>
                          <Text fontSize="xs" color="gray.700" fontWeight={isPaid ? "semibold" : "normal"}>
                            {formatCurrency(subtotalAmount)}
                          </Text>
                        </Flex>
                        {gstAmount > 0 && (
                          <Flex justify="space-between" align="center">
                            <Text fontSize="2xs" color="gray.500" fontWeight="medium">GST</Text>
                            <Text fontSize="xs" color="gray.700" fontWeight={isPaid ? "semibold" : "normal"}>
                              {formatCurrency(gstAmount)}
                            </Text>
                          </Flex>
                        )}
                        {invoice.invoice_line_items && invoice.invoice_line_items.length > 0 && (
                          <Box>
                            <Text fontSize="2xs" color="gray.500" fontWeight="medium" mb={0.5}>Description</Text>
                            <Text fontSize="xs" color="gray.700" noOfLines={2}>
                              {invoice.invoice_line_items.map(item => item.description).join(", ")}
                            </Text>
                          </Box>
                        )}
                      </VStack>
                    </Box>
                  );
                })}
              </VStack>

              {/* Desktop Table View */}
              <Box overflowX="auto" w="100%" display={{ base: "none", md: "block" }}>
                <Table variant="simple" size="sm" bg="white">
                <Thead bg="gray.100">
                  <Tr>
                    <Th>Invoice Date</Th>
                    <Th>Due Date</Th>
                    <Th>Due in</Th>
                    <Th>Description</Th>
                    <Th isNumeric>Subtotal</Th>
                    <Th isNumeric>GST</Th>
                    <Th isNumeric>Total</Th>
                    <Th>Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {visibleInvoices.map((invoice) => {
                    const isPaid = invoice.status === 'paid' || invoice.status === 'Paid';
                    const subtotalAmount = invoice.invoice_line_items && invoice.invoice_line_items.length > 0
                      ? invoice.invoice_line_items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0)
                      : 0;
                    const gstAmount = invoice.gst_amount || 0;
                    const totalAmount = subtotalAmount + gstAmount;
                    const dueInBadge = getDueInBadge(invoice);
                    return (
                      <Tr 
                        key={invoice.id}
                        bg={isPaid ? "green.50" : "white"}
                        _hover={{ bg: isPaid ? "green.100" : "gray.50" }}
                        borderLeft={isPaid ? "4px solid" : "none"}
                        borderColor={isPaid ? "green.400" : "transparent"}
                      >
                        <Td fontWeight={isPaid ? "semibold" : "normal"}>
                          {formatInvoiceDate(invoice.invoice_date)}
                        </Td>
                        <Td fontWeight={isPaid ? "semibold" : "normal"}>
                          {formatInvoiceDate(invoice.due_date)}
                        </Td>
                        <Td>{dueInBadge ? <Badge bg={dueInBadge.bg} color={dueInBadge.color} fontSize="xs" px={2} py={1}>{dueInBadge.label}</Badge> : <Text as="span" color="gray.400">—</Text>}</Td>
                        <Td fontWeight={isPaid ? "semibold" : "normal"}>
                          {invoice.invoice_line_items && invoice.invoice_line_items.length > 0 
                            ? invoice.invoice_line_items.map(item => item.description).join(", ")
                            : "-"}
                        </Td>
                        <Td isNumeric fontWeight={isPaid ? "semibold" : "normal"}>
                          {formatCurrency(subtotalAmount)}
                        </Td>
                        <Td isNumeric fontWeight={isPaid ? "semibold" : "normal"} color={gstAmount > 0 ? "gray.700" : "gray.400"}>
                          {gstAmount > 0 ? formatCurrency(gstAmount) : "-"}
                        </Td>
                        <Td isNumeric fontWeight="bold" color={isPaid ? "green.600" : "gray.800"}>
                          {formatCurrency(totalAmount)}
                        </Td>
                        <Td>
                          <Badge colorScheme={isPaid ? 'green' : 'orange'} fontSize="sm" px={2} py={1}>
                            {invoice.status}
                          </Badge>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
              </Box>
            </Box>
            );
          })()}

          {/* Documents Section */}
          <Box mb={{ base: 6, md: 8 }}>
            <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} mb={4} flexDirection={{ base: "column", md: "row" }} gap={3}>
              <Heading size={{ base: "xs", md: "sm" }} color="brand.700">
                Enrollment Documents
              </Heading>
              <HStack spacing={2} flexWrap="wrap" w={{ base: "100%", md: "auto" }}>
                {isPolling && documents.length > 0 && (
                  <Alert status="info" size="sm" borderRadius="md" px={3} py={2} w={{ base: "100%", md: "auto" }}>
                    <Spinner size="xs" mr={2} />
                    <Text fontSize={{ base: "2xs", md: "xs" }}>
                      Generating documents... {currentDocCount}/{documents.length} ready
                    </Text>
                  </Alert>
                )}
                {(() => {
                  const undownloadedPdfs = documents.filter(
                    d => d.status === 'done' && d.isPdf && !downloadedDocs.has(d.id || d.title)
                  );
                  return undownloadedPdfs.length > 0 && (
                    <Button
                      leftIcon={<DownloadIcon />}
                      colorScheme="teal"
                      size={{ base: "xs", md: "sm" }}
                      onClick={downloadAllDocumentsAsPdf}
                      isLoading={downloadingAll}
                      loadingText="Downloading..."
                      w={{ base: "100%", md: "auto" }}
                      sx={{ fontWeight: 500, letterSpacing: "normal" }}
                    >
                      <Box as="span" display={{ base: "none", sm: "inline" }}>Download All PDFs ({undownloadedPdfs.length})</Box>
                      <Box as="span" display={{ base: "inline", sm: "none" }}>Download All ({undownloadedPdfs.length})</Box>
                    </Button>
                  );
                })()}
              </HStack>
            </Flex>

            {documents.length > 0 ? (
              <VStack spacing={3} align="stretch">
                {documents.map((doc, index) => {
                  const isDone = doc.status === 'done';
                  const isGenerating = doc.status === 'generating';
                  const isNotStarted = doc.status === 'not_started';

                  return (
                    <Flex
                      key={doc.id || index}
                      align={{ base: "flex-start", md: "center" }}
                      p={{ base: 3, md: 4 }}
                      border="1px solid"
                      borderColor={
                        isDone ? "green.200" : 
                        (isGenerating || isNotStarted) ? "yellow.200" : 
                        "gray.200"
                      }
                      borderRadius="md"
                      bg={
                        isDone ? "white" : 
                        (isGenerating || isNotStarted) ? "yellow.50" : 
                        "gray.50"
                      }
                      justify="space-between"
                      _hover={isDone ? { bg: "gray.50", borderColor: "brand.400" } : {}}
                      transition="all 0.2s"
                      flexDirection={{ base: "column", md: "row" }}
                      gap={{ base: 3, md: 0 }}
                    >
                      <HStack spacing={3} flex="1" mr={{ base: 0, md: 4 }} w={{ base: "100%", md: "auto" }}>
                        {isDone ? (
                          <Box color="green.500" fontSize={{ base: "20px", md: "24px" }}>
                            <CheckCircleIcon />
                          </Box>
                        ) : (isGenerating || isNotStarted) ? (
                          <Spinner size="sm" color="yellow.500" />
                        ) : (
                          <Box color="gray.400" fontSize={{ base: "20px", md: "24px" }}>
                            <FaFileAlt />
                          </Box>
                        )}
                        <VStack align="start" spacing={0} flex={1} minW={0}>
                          <HStack spacing={2} flexWrap="wrap">
                            <Text fontWeight="semibold" fontSize={{ base: "sm", md: "md" }} color={isDone ? "gray.900" : (isGenerating || isNotStarted) ? "gray.700" : "gray.500"} noOfLines={2}>
                              {getDisplayDocumentName(doc.title)}
                            </Text>
                            {isDone && (
                              <Badge colorScheme="green" size={{ base: "xs", md: "sm" }}>Done</Badge>
                            )}
                            {(isGenerating || isNotStarted) && (
                              <Badge colorScheme="yellow" size={{ base: "xs", md: "sm" }}>Generating</Badge>
                            )}
                          </HStack>
                          <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500">
                            {isDone 
                              ? 'PDF Document ready' 
                              : (isGenerating || isNotStarted)
                              ? 'Please wait while this document is being generated...'
                              : 'Document generation has not started yet'}
                          </Text>
                        </VStack>
                      </HStack>
                      <HStack spacing={2} w={{ base: "100%", md: "auto" }} justify={{ base: "flex-end", md: "auto" }}>
                        <Button
                          size={{ base: "xs", md: "sm" }}
                          variant="outline"
                          leftIcon={<ViewIcon />}
                          onClick={() => viewDocument(doc)}
                          isDisabled={!isDone}
                          flex={{ base: 1, md: "none" }}
                          sx={{ fontWeight: 500, letterSpacing: "normal" }}
                        >
                          View
                        </Button>
                        <Button
                          size={{ base: "xs", md: "sm" }}
                          colorScheme={downloadedDocs.has(doc.id || doc.title) ? "green" : "teal"}
                          leftIcon={downloadedDocs.has(doc.id || doc.title) ? <CheckCircleIcon /> : <DownloadIcon />}
                          onClick={() => downloadDocumentAsPdf(doc)}
                          isLoading={downloadingDoc === doc.title}
                          loadingText="Downloading..."
                          isDisabled={!isDone}
                          flex={{ base: 1, md: "none" }}
                          sx={{ fontWeight: 500, letterSpacing: "normal" }}
                        >
                          <Box as="span" display={{ base: "none", sm: "inline" }}>{downloadedDocs.has(doc.id || doc.title) ? "Downloaded" : "Download PDF"}</Box>
                          <Box as="span" display={{ base: "inline", sm: "none" }}>{downloadedDocs.has(doc.id || doc.title) ? "Done" : "Download"}</Box>
                        </Button>
                      </HStack>
                    </Flex>
                  );
                })}
              </VStack>
            ) : (
              <Box p={6} bg="gray.50" borderRadius="md" textAlign="center">
                <Text color="gray.600">No documents available at this time.</Text>
              </Box>
            )}
          </Box>
        </Box>
      </Container>

      {/* Document Viewer Modal - Improved desktop layout */}
      <Modal 
        isOpen={isDocOpen} 
        onClose={onDocClose} 
        size={{ base: "full", md: "6xl" }} 
        scrollBehavior="inside"
        isCentered={{ base: false, md: true }}
      >
        <ModalOverlay />
        <ModalContent 
          maxW={{ base: "100vw", md: "90vw" }} 
          h={{ base: "100vh", md: "90vh" }} 
          maxH={{ base: "100vh", md: "90vh" }} 
          m={{ base: 0, md: "auto" }} 
          borderRadius={{ base: 0, md: "xl" }} 
          display="flex" 
          flexDirection="column"
          overflow="hidden"
        >
          <ModalHeader color="brand.700" borderBottom="2px solid" borderColor="brand.300" fontSize={{ base: "sm", md: "md" }} py={{ base: 2, md: 4 }} px={{ base: 3, md: 6 }} flexShrink={0}>
            {getDisplayDocumentName(activeDocument?.title) || "Document"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={0} flex="1" overflow="hidden" display="flex" flexDirection="column" bg="gray.100">
            {activeDocument && activeDocument.isPdf && activeDocument.pdfFileId ? (
              <Box
                p={0}
                flex="1"
                display="flex"
                flexDirection="column"
                minH={0}
                w="full"
              >
                {/* Display PDF using Google Drive viewer in iframe - file is public so should work */}
                <iframe
                  src={`https://drive.google.com/file/d/${activeDocument.pdfFileId}/preview`}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    flex: 1,
                  }}
                  title={activeDocument.title}
                />
              </Box>
            ) : (
              <Box p={{ base: 4, md: 6 }} textAlign="center" my="auto">
                <VStack spacing={4}>
                  <Spinner size="xl" color="brand.500" thickness="4px" />
                  <Text color="gray.600" fontSize={{ base: "sm", md: "md" }}>
                    {activeDocument?.status === 'generating' 
                      ? 'Document is being generated. Please wait...'
                      : activeDocument?.status === 'not_started'
                      ? 'Document generation has not started yet.'
                      : 'Document not available yet.'}
                  </Text>
                </VStack>
              </Box>
            )}
          </ModalBody>
          <ModalFooter flexDirection={{ base: "column", md: "row" }} gap={2} py={{ base: 2, md: 4 }} px={{ base: 3, md: 6 }} flexShrink={0} bg="white">
            <Button colorScheme="brand" variant="outline" onClick={onDocClose} w={{ base: "100%", md: "auto" }} order={{ base: 2, md: 1 }} size={{ base: "sm", md: "md" }}>
              Close
            </Button>
            {activeDocument && (
              <Button
                colorScheme={activeDocument.isPdf && downloadedDocs.has(activeDocument.id || activeDocument.title) ? "green" : "teal"}
                leftIcon={activeDocument.isPdf && downloadedDocs.has(activeDocument.id || activeDocument.title) ? <CheckCircleIcon /> : <DownloadIcon />}
                onClick={() => downloadDocumentAsPdf(activeDocument)}
                isLoading={downloadingDoc === activeDocument.title}
                isDisabled={!activeDocument.isPdf || !activeDocument.pdfFileId}
                w={{ base: "100%", md: "auto" }}
                order={{ base: 1, md: 2 }}
                size={{ base: "sm", md: "md" }}
                boxShadow="sm"
                sx={{ fontWeight: 500, letterSpacing: "normal" }}
              >
                {activeDocument.isPdf && downloadedDocs.has(activeDocument.id || activeDocument.title) ? "Downloaded" : "Download PDF"}
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};